import * as React from 'react';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { StylesProps } from 'theme/jss-types';
import { FormField } from 'components/create-consignment/create-consignment.types';
import { useTranslation } from 'react-i18next';
import {
    Col,
    Form,
    FormInstance,
    Select,
    Tooltip,
} from 'antd';
import GenericField from '../generic-field';
import { searchProducts, getProductDetails } from 'network/consignments.api';
import { CheckCircleFilled } from '@ant-design/icons';
import { useDebounce } from 'hooks/use-debounce';
import { formFields, dangerousGoodFields } from 'components/create-consignment/create-modal.constants';

const {
    Unit,
    WeightUnit,
} = formFields;

const {
    DangerousGoodUnNumber,
    IsDangerousGood,
} = dangerousGoodFields;

const styles = () => ({
    formItem: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'left',
    },
    title: {
        color: '#333333',
        fontFamily: 'Open Sans',
        fontSize: 12,
        letterSpacing: 0,
        width: '100%',
        marginBottom: 4,
        marginLeft: 4,
    },
    customTooltip: {
        '& .ant-tooltip-inner': {
            color: '#333',
        },
    },
});

interface IProps extends StylesProps<ReturnType<typeof styles>> {
    form: FormInstance;
    params: any;
    field: FormField;
}

const ProductCodeField = (props: IProps) => {
    const { t } = useTranslation();
    const {
        classes,
        params,
        field,
        form,
    } = props;

    const {
        key,
        pieceIndex,
        productCodeLists,
        showProductCodeDropdown,
        productHsCodeMapping,
        getValidationRules,
        getIsRequiredValidation,
        productCodeActive,
        setDisabledFields,
        disabledFields,
        setProductCode,
    } = params;
    const isPieceLevel = pieceIndex !== null && pieceIndex !== undefined;

    const [options, setOptions] = React.useState<any[]>([]);
    const [productList, setProductList] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [success, setSuccess] = React.useState<boolean>(false);
    const [searchValue, setSearchValue] = React.useState<string>('');

    const declaredCurrencyKey = isPieceLevel
        ? `piece_declaredCurrency_${pieceIndex}`
        : 'declaredCurrency';
    const hsCodeKey = isPieceLevel
        ? `piece_hsCode_${pieceIndex}`
        : 'hsCode';

    const [existingDeclaredCurrency, setExistingDeclaredCurrency] = React.useState<string | undefined>(
        form.getFieldValue(declaredCurrencyKey),
    );

    const debouncedSearchValue = useDebounce(searchValue);

    React.useEffect(() => {
        const list = productCodeLists[pieceIndex] || [];
        setOptions(list.map((item: any) => ({
            label: item,
            value: item,
        })));
    }, [productCodeLists]);

    const handleProductCodeChange = (value: any) => {
        const mapping = productHsCodeMapping[pieceIndex] || {};
        const requiredHsCode = mapping[value];
        if (requiredHsCode) {
            props?.form.setFieldsValue({
                [hsCodeKey]: requiredHsCode,
            });
        }
    };

    React.useEffect(() => {
        const searchProductsAsync = async () => {
            if (debouncedSearchValue?.length < 2) return;
            setLoading(true);
            const response = await searchProducts({ searchKey: debouncedSearchValue });
            if (response?.isSuccess && Array.isArray(response?.data)) {
                setProductList(response.data || []);
            }
            setLoading(false);
        };
        searchProductsAsync();
    }, [debouncedSearchValue]);

    const handleSearch = (value: string) => {
        setSearchValue(value);
    };

    const updateFieldsFromProductData = (productData: any, resetFields: boolean = false) => {
        const newDisabledFields: Record<string, boolean> = { ...disabledFields };
        const fieldsToUpdate: any = {
            length: productData?.length,
            width: productData?.width,
            height: productData?.height,
            weight: productData?.weight,
            description: productData?.description,
            declaredValue: productData?.declaredValue,
            declaredCurrency: productData?.declaredCurrency,
            dangerousGoodUnNumber: productData?.dangerousGoodUnNumber,
            isDangerousGood: productData?.dangerousGoodUnNumber
                && productData?.dangerousGoodUnNumber?.length > 0 ? 'true' : 'false',
        };

        Object.entries(fieldsToUpdate).forEach(([_field]) => {
            const fieldKey = isPieceLevel ? `piece_${_field}_${pieceIndex}` : _field;
            if (_field === 'length') {
                const unitKey = isPieceLevel ? `piece_${Unit.key}_${pieceIndex}` : Unit.key;
                newDisabledFields[unitKey] = !resetFields;
            }
            if (_field === 'weight') {
                const weightUnitKey = isPieceLevel ? `piece_${WeightUnit.key}_${pieceIndex}` : WeightUnit.key;
                newDisabledFields[weightUnitKey] = !resetFields;
            }
            newDisabledFields[fieldKey] = !resetFields;

            const hasValidUnNumber = productData?.dangerousGoodUnNumber && productData?.dangerousGoodUnNumber?.length > 0;
            if (_field === 'dangerousGoodUnNumber') {
                const dangerousGoodUnNumberKey = isPieceLevel
                    ? `piece_${DangerousGoodUnNumber.key}_${pieceIndex}`
                    : DangerousGoodUnNumber.key;
                newDisabledFields[dangerousGoodUnNumberKey] = !resetFields
                && hasValidUnNumber;
            }
            if (_field === 'isDangerousGood') {
                const isDangerousGoodKey = isPieceLevel ? `piece_${IsDangerousGood.key}_${pieceIndex}` : IsDangerousGood.key;
                newDisabledFields[isDangerousGoodKey] = !resetFields
                && hasValidUnNumber;
            }
        });

        setDisabledFields(newDisabledFields);

        const formUpdates: Record<string, any> = {};
        Object.entries(fieldsToUpdate).forEach(([_field, value]) => {
            const fieldKey = isPieceLevel ? `piece_${_field}_${pieceIndex}` : _field;
            if (_field !== 'declaredCurrency' || (_field === 'declaredCurrency' && value)) {
                formUpdates[fieldKey] = resetFields ? undefined : value;
            }

            if (_field === 'isDangerousGood' && !resetFields) {
                formUpdates[fieldKey] = value;
            } else if (_field === 'isDangerousGood' && resetFields) {
                formUpdates[fieldKey] = 'false';
            }

            if (_field === 'dangerousGoodUnNumber' && resetFields) {
                formUpdates[fieldKey] = undefined;
            }
        });

        if (resetFields) {
            formUpdates[declaredCurrencyKey] = existingDeclaredCurrency;
        }

        props?.form.setFieldsValue(formUpdates);
        setProductCode(productData?.code || '');
    };

    const handleSelect = async (option: any) => {
        const result = await getProductDetails({ code: option.value });
        if (result.isSuccess && result?.data?.code) {
            setSuccess(true);
            setExistingDeclaredCurrency(
                props?.form.getFieldValue(declaredCurrencyKey),
            );
            updateFieldsFromProductData(result?.data);
        }
    };

    const renderField = () => {
        if (!productCodeActive) {
            return (
                <Col span={24}>
                    <GenericField {...props} colSpan={24} />
                </Col>
            );
        }
        return (
            <Col span={24}>
                <div className={classes.formItem}>
                    <div
                        className={classes.title}
                        style={{ width: '100%' }}
                    >
                        {t('pieceProductCode')}
                        {getIsRequiredValidation(field.key, true) && '*'}
                    </div>
                    <Form.Item
                        name={key}
                        initialValue={field.defaultValue}
                        valuePropName={field.valuePropName}
                        rules={getValidationRules(field, true)}
                        style={{ marginBottom: '0' }}
                        extra={success ? (
                            <span style={{ fontSize: 12, position: 'relative', top: '-3px' }}>
                                <CheckCircleFilled style={{ color: '#52c41a', marginRight: 6 }} />
                                Product found. Auto-populated product details.
                            </span>
                        ) : null}
                    >
                        <Select
                            notFoundContent={null}
                            placeholder={t('pieceProductCode')}
                            options={
                                productList?.map((data: any) => {
                                    return {
                                        label: `${data.name} (${data.code})`,
                                        value: data.code,
                                    };
                                })
                            }
                            onSelect={(val, option) => handleSelect(option)}
                            onChange={(val) => {
                                if (!val) {
                                    setSuccess(false);
                                    updateFieldsFromProductData({}, true);
                                }
                            }}
                            defaultActiveFirstOption={false}
                            filterOption={false}
                            onSearch={handleSearch}
                            loading={loading}
                            showSearch
                            allowClear
                        />
                    </Form.Item>
                </div>
            </Col>
        );
    };

    return (
        <div style={{ display: 'flex', width: '50%' }}>
            {showProductCodeDropdown
                ? (
                    <Col span={24}>
                        <Tooltip
                            title={!options.length ? 'Please validate Purchase Order Number' : ''}
                            overlayClassName={classes.customTooltip}
                            color="#FCFCFC"
                        >
                            <div className={classes.formItem}>
                                <div
                                    className={classes.title}
                                    style={{ width: '100%' }}
                                >
                                    {t('pieceProductCode')}
                                    {getIsRequiredValidation(field.key, true) && '*'}
                                </div>
                                <Form.Item
                                    name={key}
                                    initialValue={field.defaultValue}
                                    valuePropName={field.valuePropName}
                                    rules={getValidationRules(field, true)}
                                >
                                    <Select
                                        showSearch
                                        disabled={!options.length}
                                        placeholder={t(field.key)}
                                        options={options}
                                        allowClear
                                        onChange={handleProductCodeChange}
                                    />
                                </Form.Item>
                            </div>
                        </Tooltip>
                    </Col>

                )
                : renderField()}
        </div>
    );
};

const hocConfig: HocOptions = {
    connectJss: {
        useJss: true,
        styleSheet: styles,
    },
};

export default GenericHoc(hocConfig)(ProductCodeField);
